import { v4 as uuidv4 } from 'uuid'
import { Theme, useTheme, DarkMode } from '@afterpaytouch/core'
import {
  CheckoutAppSelector,
  ConvergenceState,
  useAppSelector,
  useAppDispatch,
  useIsAfterpayAppGiftCardFlow,
  useAuthentication,
  useIsTrustedCheckout,
  useCountry,
  useMerchantId,
  useConsumerEmail,
  useCashEligibilitySignals,
  useCashCreationDate,
  useOrderSource,
  setCashEligibilitySignal,
  setCashCreationDate,
  setShadowAccountExists,
  setCashLoginType,
} from '..'
import { PaymentType } from '@afterpay/types'
import {
  ConvergenceRollout,
  ConvergenceTrackingProps,
  ConvergenceAuthMethod,
  ConvergenceUnavailable,
  Features,
  APT_USER_ID,
  allowedPaymentTypeByRolloutAndCohort,
  availableRollouts,
  ConvergenceCashLogin,
  PayMonthlyRedesignRollout,
} from '../../model'
import { createSelector } from '@reduxjs/toolkit'
import { updateTheme } from '../../utils/convergence'
import { getEmailDomain, tryGetStoredUUIDFromLocalStorage } from './helper'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { parseCookies } from 'nookies'
import { useFlag, useLDClient } from '@afterpaytouch/feature'
import { Cohort } from '@afterpaytouch/portal-api'
import { CashEligibilitySignals, CashLoginType } from '@afterpaytouch/portal-api/consumer/lookup'
import { setCohort } from './reducer'
import { isInAppBrowser } from '../../utils/device'
import { buildTargetingKey } from '../../utils/launchDarkly/targetingKey'

export const convergenceSelector: CheckoutAppSelector<ConvergenceState> = (state) => state.convergence
export const convergenceRolloutSelector: CheckoutAppSelector<ConvergenceRollout> = createSelector(convergenceSelector, (state) => state.rollout)
export const convergenceCohortSelector: CheckoutAppSelector<Cohort | undefined> = createSelector(convergenceSelector, (state) => state.cohort)
export const convergenceLoginMethodSelector: CheckoutAppSelector<ConvergenceAuthMethod | undefined> = createSelector(
  convergenceSelector,
  (state) => state.loginMethod
)
export const cashLoginAvailabilitySelector: CheckoutAppSelector<ConvergenceCashLogin | undefined> = createSelector(
  convergenceSelector,
  (state) => state.cashLoginEnabled
)
export const ldKeySelector: CheckoutAppSelector<string | undefined> = createSelector(convergenceSelector, (state) => state.ldKey)

export const convergenceHasSetThemeBeforeSelector: CheckoutAppSelector<boolean> = createSelector(convergenceSelector, (state) => state.hasSetThemeBefore)

/** Hooks */
export const useCashLoginAvailability = (): ConvergenceCashLogin | undefined => useAppSelector(cashLoginAvailabilitySelector)

export const useCheckoutTheme = (): [Theme, (theme: Theme) => Promise<void>] => {
  const {
    theme: { selected, setTheme },
  } = useTheme()

  const setThemeAction = async (theme: Theme): Promise<void> => {
    setTheme(theme)
    await updateTheme({ theme })
  }

  return [selected, setThemeAction]
}

export const useCheckoutDarkMode = (): [DarkMode, (darkMode: DarkMode) => Promise<void>] => {
  const {
    theme: { darkMode, setDarkMode },
  } = useTheme()

  const setDarkModeAction = async (darkMode: DarkMode): Promise<void> => {
    setDarkMode(darkMode)
  }

  return [darkMode, setDarkModeAction]
}

export const useHasSetThemeBefore = (): boolean => useAppSelector(convergenceHasSetThemeBeforeSelector)

export const useCheckoutDarkModePreference = (): 'dark' | 'light' | 'system' => {
  const cookies = parseCookies()
  const cookieValue = cookies['apt-dark-mode'] as 'dark' | 'light' | 'system'
  if (['dark', 'light', 'system'].includes(cookieValue)) {
    return cookieValue
  }
  return 'system'
}

export const useRollout = (): ConvergenceRollout => useAppSelector(convergenceRolloutSelector)
export const useCohort = (): Cohort | undefined => useAppSelector(convergenceCohortSelector)
export const useLoginMethod = (): ConvergenceAuthMethod | undefined => useAppSelector(convergenceLoginMethodSelector)

export const usePaymentTypeAllowInConvergence = (paymentType: PaymentType): boolean => {
  const { cohort, rollout } = useAppSelector(convergenceSelector)
  const isCashPayMonthlyEnabled = useFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED, false)

  if (!rollout || !cohort) {
    return false
  }

  const defaultWhitelist = allowedPaymentTypeByRolloutAndCohort[rollout]?.[cohort]
  if (!defaultWhitelist) {
    return false
  }

  const testCashOnlyWhitelist = Array.from(new Set([...defaultWhitelist, PaymentType.PCL]))
  const isConvergenceAvailable = availableRollouts.includes(rollout)
  const whitelist = isCashPayMonthlyEnabled && cohort === Cohort.CashOnly && isConvergenceAvailable ? testCashOnlyWhitelist : defaultWhitelist

  return whitelist?.includes(paymentType) ?? false
}

export const useConvergenceTrackingProps = (): ConvergenceTrackingProps => {
  const [checkoutBrand] = useCheckoutTheme()
  const customerCohort = useCohort()
  const convergenceRollout = useRollout()
  const cashLoginAvailability = useCashLoginAvailability()

  return {
    customerCohort,
    checkoutBrand,
    convergenceRollout,
    ...(cashLoginAvailability !== undefined && { cashLoginAvailability }),
  }
}

export const useAPFooter = (): boolean => {
  const customerCohort = useCohort()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const auth = useAuthentication()
  const isTrustedCheckout = useIsTrustedCheckout()

  if (isAfterpayAppGiftCardFlow || auth.persistent || isTrustedCheckout) {
    return true
  }

  if (customerCohort === Cohort.CashOnly || customerCohort === undefined) {
    return false
  }
  return true
}

export const useRolloutFromClient = (): (() => Promise<{
  rollout: ConvergenceRollout
  cashLogin: ConvergenceCashLogin
}>) => {
  const email = useConsumerEmail()
  const cookies = parseCookies()
  const isIAB = isInAppBrowser()
  const ldKey = useAppSelector(ldKeySelector)

  const storedUuid = cookies[APT_USER_ID] ?? tryGetStoredUUIDFromLocalStorage()
  const country = useCountry()
  const merchantId = useMerchantId()

  const cohort = useCohort() ?? Cohort.APOnly

  const emailDomain = getEmailDomain(email)
  const userAgent = navigator.userAgent
  const source = useOrderSource()

  const serverLdKey = isUndefinedOrEmpty(ldKey) ? uuidv4() : ldKey
  const key = isUndefinedOrEmpty(storedUuid) ? serverLdKey : storedUuid
  const targetingKey = buildTargetingKey({
    key: key!,
    cohort,
  })

  const cashEligibilitySignals = useCashEligibilitySignals()
  const hasEmail = cashEligibilitySignals?.hasEmail ?? false
  const hasSmsNumber = cashEligibilitySignals?.hasSmsNumber ?? false
  const hasVerifiedIdentity = cashEligibilitySignals?.hasVerifiedIdentity ?? false
  const hasLinkedDebit = cashEligibilitySignals?.hasLinkedDebit ?? false
  const mostRecentActivityAt = cashEligibilitySignals?.mostRecentActivityAt ?? 'none'
  const hasValidRegion = cashEligibilitySignals?.hasValidRegion ?? false

  const cashCreationDate = useCashCreationDate()

  const ldClient = useLDClient()

  return async () => {
    const context = {
      kind: 'user',
      key,
      anonymous: isUndefinedOrEmpty(storedUuid),
      country,
      merchantId,
      cohort,
      targetingKey,
      userAgent,
      // note: emailDomain is used for internal staff targeting only in production
      emailDomain,
      hasEmail,
      hasSmsNumber,
      hasVerifiedIdentity,
      hasLinkedDebit,
      mostRecentActivityAt,
      hasValidRegion,
      cashCreationDate,
      isInAppBrowser: isIAB,
      source,
    }

    if (ldClient !== undefined) {
      await ldClient.identify(context)

      const rollout: ConvergenceRollout = ldClient?.variation(Features.CONVERGENCE_ROLLOUT, ConvergenceUnavailable.Unavailable)

      const cashLogin: ConvergenceCashLogin = ldClient?.variation(Features.CONVERGENCE_LOGIN_EXTENDED, ConvergenceCashLogin.DisabledNew)

      return { rollout, cashLogin }
    }

    return {
      rollout: ConvergenceUnavailable.Unavailable,
      cashLogin: ConvergenceCashLogin.DisabledNew,
    }
  }
}

export const useIsPayMonthlyGARollout = (): boolean => {
  const redesignRollout = useFlag<PayMonthlyRedesignRollout>(Features.CHECKOUT_PAY_MONTHLY_REDESIGN_ROLLOUT, PayMonthlyRedesignRollout.DEFAULT)
  return redesignRollout === PayMonthlyRedesignRollout.GA
}

export interface CashCohortLookupData {
  cohort: Cohort
  cashEligibilitySignals?: CashEligibilitySignals | null
  cashCreationDate?: number
  shadowAccountExists?: boolean
  cashLoginType?: CashLoginType
}

export const useSetCashCohortLookupData = (): ((data: CashCohortLookupData) => void) => {
  const dispatch = useAppDispatch()

  return (data: CashCohortLookupData) => {
    dispatch(setCohort(data.cohort))
    dispatch(setCashEligibilitySignal(data.cashEligibilitySignals))
    dispatch(setCashCreationDate(data.cashCreationDate))
    dispatch(setShadowAccountExists(data.shadowAccountExists))
    dispatch(setCashLoginType(data.cashLoginType))
  }
}
